import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Badge } from '../ui/badge';
import { Textarea } from '../ui/textarea';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../ui/select';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '../ui/dialog';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from '../ui/alert-dialog';
import { Switch } from '../ui/switch';
import {
  Plus, Trash2, Copy, Edit2, Ruler, Square, Hash, ChevronDown, ChevronRight,
  AlertTriangle, Loader2, Paintbrush, Wrench, Calculator, Upload, FileSpreadsheet, X,
  Link2, RefreshCw, Image, FileText, Download, ExternalLink, CheckCircle2, XCircle, Clock, Zap,
  Box, Eye, Maximize2, RotateCw, Layers, Home, Building
} from 'lucide-react';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../ui/tabs';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// ============================================================================
// HOVER INTEGRATION COMPONENTS
// ============================================================================

const HoverConnectionStatus = ({ status, onConnect, onRefresh, isAdmin, webhookStatus }) => {
  const getStatusBadge = () => {
    if (!status) return null;
    if (status.connected && status.token_valid) {
      return (
        <Badge className="bg-emerald-100 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-400">
          <CheckCircle2 className="h-3 w-3 mr-1" />
          Connected
        </Badge>
      );
    }
    if (status.connected && !status.token_valid) {
      return (
        <Badge className="bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-400">
          <Clock className="h-3 w-3 mr-1" />
          Token Expired
        </Badge>
      );
    }
    return (
      <Badge variant="outline" className="text-slate-500">
        <XCircle className="h-3 w-3 mr-1" />
        Not Connected
      </Badge>
    );
  };

  const getWebhookBadge = () => {
    if (!webhookStatus) return null;
    if (webhookStatus.registered) {
      return (
        <Badge className="bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400">
          <Zap className="h-3 w-3 mr-1" />
          Auto-Sync On
        </Badge>
      );
    }
    return null;
  };

  return (
    <div className="flex items-center gap-3 flex-wrap">
      <div className="flex items-center gap-2">
        <Image className="h-4 w-4 text-blue-500" />
        <span className="text-sm font-medium">Hover</span>
        {getStatusBadge()}
        {getWebhookBadge()}
      </div>
      {isAdmin && !status?.connected && (
        <Button size="sm" variant="outline" onClick={onConnect}>
          <Link2 className="h-3 w-3 mr-1" />
          Connect
        </Button>
      )}
      {status?.connected && !status?.token_valid && isAdmin && (
        <Button size="sm" variant="outline" onClick={onRefresh}>
          <RefreshCw className="h-3 w-3 mr-1" />
          Refresh
        </Button>
      )}
    </div>
  );
};

const HoverJobLinkDialog = ({ open, onOpenChange, onLink, loading }) => {
  const [hoverJobId, setHoverJobId] = useState('');

  const handleSubmit = () => {
    if (!hoverJobId.trim()) {
      toast.error('Please enter a Hover Job ID');
      return;
    }
    onLink(hoverJobId.trim());
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Link Hover Job</DialogTitle>
          <DialogDescription>
            Enter the Hover Job ID to link measurements and photos to this job.
            You can find the Job ID in the Hover URL (e.g., hover.to/jobs/12345)
          </DialogDescription>
        </DialogHeader>
        <div className="py-4">
          <Label htmlFor="hover-job-id">Hover Job ID</Label>
          <Input
            id="hover-job-id"
            value={hoverJobId}
            onChange={(e) => setHoverJobId(e.target.value)}
            placeholder="e.g., 12345678"
            className="mt-2"
          />
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
          <Button onClick={handleSubmit} disabled={loading}>
            {loading ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Link2 className="h-4 w-4 mr-2" />}
            Link Job
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

const HoverPhotosGallery = ({ photos, loading }) => {
  const [selectedPhoto, setSelectedPhoto] = useState(null);

  if (loading) {
    return (
      <div className="flex items-center justify-center py-8">
        <Loader2 className="h-6 w-6 animate-spin text-blue-500" />
      </div>
    );
  }

  if (!photos || photos.length === 0) {
    return (
      <div className="text-center py-8 text-slate-500">
        <Image className="h-8 w-8 mx-auto mb-2 opacity-50" />
        <p className="text-sm">No Hover photos available</p>
      </div>
    );
  }

  return (
    <>
      <div className="grid grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-2">
        {photos.map((photo, idx) => (
          <div
            key={photo.id || idx}
            className="aspect-square rounded-lg overflow-hidden cursor-pointer hover:ring-2 hover:ring-blue-500 transition-all"
            onClick={() => setSelectedPhoto(photo)}
          >
            <img
              src={photo.url}
              alt={photo.label || `Photo ${idx + 1}`}
              className="w-full h-full object-cover"
              loading="lazy"
            />
          </div>
        ))}
      </div>

      {/* Photo Modal */}
      <Dialog open={!!selectedPhoto} onOpenChange={() => setSelectedPhoto(null)}>
        <DialogContent className="max-w-4xl">
          <DialogHeader>
            <DialogTitle>{selectedPhoto?.label || 'Hover Photo'}</DialogTitle>
          </DialogHeader>
          <div className="flex justify-center">
            <img
              src={selectedPhoto?.url}
              alt={selectedPhoto?.label || 'Photo'}
              className="max-h-[70vh] object-contain rounded-lg"
            />
          </div>
          <DialogFooter>
            <Button variant="outline" asChild>
              <a href={selectedPhoto?.url} target="_blank" rel="noopener noreferrer">
                <Download className="h-4 w-4 mr-2" />
                Download
              </a>
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
};

const HoverDeliverablesPanel = ({ deliverables, loading }) => {
  const getDeliverableIcon = (type) => {
    switch (type?.toLowerCase()) {
      case 'pdf': return FileText;
      case 'xlsx': case 'xls': return FileSpreadsheet;
      default: return FileText;
    }
  };

  const getDeliverableBadge = (type) => {
    const colors = {
      pdf: 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400',
      xlsx: 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400',
      xls: 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400',
      xml: 'bg-purple-100 text-purple-700 dark:bg-purple-900/30 dark:text-purple-400',
      skp: 'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400',
    };
    return colors[type?.toLowerCase()] || 'bg-slate-100 text-slate-700';
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-6">
        <Loader2 className="h-5 w-5 animate-spin text-blue-500" />
      </div>
    );
  }

  if (!deliverables || deliverables.length === 0) {
    return (
      <div className="text-center py-6 text-slate-500">
        <FileText className="h-6 w-6 mx-auto mb-2 opacity-50" />
        <p className="text-sm">No deliverables available</p>
      </div>
    );
  }

  return (
    <div className="space-y-2">
      {deliverables.map((deliv, idx) => {
        const Icon = getDeliverableIcon(deliv.type);
        return (
          <div
            key={deliv.id || idx}
            className="flex items-center justify-between p-3 rounded-lg border border-slate-200 dark:border-slate-700 hover:bg-slate-50 dark:hover:bg-slate-800/50"
          >
            <div className="flex items-center gap-3">
              <Icon className="h-5 w-5 text-slate-400" />
              <div>
                <p className="text-sm font-medium">{deliv.name}</p>
                {deliv.created_at && (
                  <p className="text-xs text-slate-500">
                    {new Date(deliv.created_at).toLocaleDateString()}
                  </p>
                )}
              </div>
              <Badge className={getDeliverableBadge(deliv.type)}>
                {deliv.type?.toUpperCase() || 'FILE'}
              </Badge>
            </div>
            {deliv.url && (
              <Button size="sm" variant="ghost" asChild>
                <a href={deliv.url} target="_blank" rel="noopener noreferrer">
                  <Download className="h-4 w-4" />
                </a>
              </Button>
            )}
          </div>
        );
      })}
    </div>
  );
};

const HoverMeasurementsSummary = ({ measurements }) => {
  if (!measurements) {
    return (
      <div className="text-center py-4 text-slate-500">
        <Calculator className="h-6 w-6 mx-auto mb-2 opacity-50" />
        <p className="text-sm">No Hover measurements available</p>
      </div>
    );
  }

  const items = [
    { label: 'Roof Area', value: measurements.roof_area_sqft, unit: 'sqft' },
    { label: 'Wall Area', value: measurements.wall_area_sqft, unit: 'sqft' },
    { label: 'Eaves', value: measurements.eaves_lf, unit: 'LF' },
    { label: 'Ridges', value: measurements.ridges_lf, unit: 'LF' },
    { label: 'Hips', value: measurements.hips_lf, unit: 'LF' },
    { label: 'Valleys', value: measurements.valleys_lf, unit: 'LF' },
    { label: 'Rakes', value: measurements.rakes_lf, unit: 'LF' },
    { label: 'Fascia', value: measurements.fascia_lf, unit: 'LF' },
    { label: 'Soffit', value: measurements.soffit_sqft, unit: 'sqft' },
    { label: 'Facets', value: measurements.facet_count, unit: '' },
  ].filter(item => item.value != null);

  return (
    <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-3">
      {items.map((item, idx) => (
        <div key={idx} className="p-3 rounded-lg bg-slate-50 dark:bg-slate-800/50">
          <p className="text-xs text-slate-500 uppercase">{item.label}</p>
          <p className="text-lg font-semibold">
            {typeof item.value === 'number' ? item.value.toLocaleString() : item.value}
            <span className="text-xs text-slate-400 ml-1">{item.unit}</span>
          </p>
        </div>
      ))}
    </div>
  );
};

// ============================================================================
// HOVER 3D/DESIGN VIEWER COMPONENT
// ============================================================================

const HoverDesignViewer = ({ 
  hoverJobId, 
  hoverStatus, 
  modelIds = [], 
  photos = [], 
  deliverables = [], 
  measurements,
  onRefresh 
}) => {
  const [activeTab, setActiveTab] = useState('overview');
  const [selectedPhoto, setSelectedPhoto] = useState(null);
  const [photoIndex, setPhotoIndex] = useState(0);

  // Get status info
  const getStatusInfo = () => {
    switch (hoverStatus) {
      case 'complete':
        return { label: 'Complete', color: 'bg-emerald-100 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-400', icon: CheckCircle2 };
      case 'processing':
        return { label: 'Processing', color: 'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400', icon: RotateCw };
      case 'queued':
        return { label: 'Queued', color: 'bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-400', icon: Clock };
      case 'uploading':
        return { label: 'Uploading', color: 'bg-purple-100 text-purple-700 dark:bg-purple-900/30 dark:text-purple-400', icon: Upload };
      case 'failed':
        return { label: 'Failed', color: 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400', icon: XCircle };
      default:
        return { label: hoverStatus || 'Unknown', color: 'bg-slate-100 text-slate-700', icon: Box };
    }
  };

  const statusInfo = getStatusInfo();
  const StatusIcon = statusInfo.icon;

  // Open Hover web app
  const openInHover = () => {
    if (hoverJobId) {
      window.open(`https://hover.to/jobs/${hoverJobId}`, '_blank');
    }
  };

  // Navigate photos
  const nextPhoto = () => {
    if (photos.length > 0) {
      setPhotoIndex((prev) => (prev + 1) % photos.length);
      setSelectedPhoto(photos[(photoIndex + 1) % photos.length]);
    }
  };

  const prevPhoto = () => {
    if (photos.length > 0) {
      setPhotoIndex((prev) => (prev - 1 + photos.length) % photos.length);
      setSelectedPhoto(photos[(photoIndex - 1 + photos.length) % photos.length]);
    }
  };

  // Get deliverable type icon and color
  const getDeliverableStyle = (type) => {
    const styles = {
      pdf: { icon: FileText, color: 'text-red-500', bg: 'bg-red-50 dark:bg-red-900/20' },
      xlsx: { icon: FileSpreadsheet, color: 'text-green-500', bg: 'bg-green-50 dark:bg-green-900/20' },
      xls: { icon: FileSpreadsheet, color: 'text-green-500', bg: 'bg-green-50 dark:bg-green-900/20' },
      xml: { icon: FileText, color: 'text-purple-500', bg: 'bg-purple-50 dark:bg-purple-900/20' },
      skp: { icon: Box, color: 'text-blue-500', bg: 'bg-blue-50 dark:bg-blue-900/20' },
    };
    return styles[type?.toLowerCase()] || { icon: FileText, color: 'text-slate-500', bg: 'bg-slate-50' };
  };

  if (!hoverJobId) {
    return (
      <div className="text-center py-12 text-slate-500">
        <Box className="h-12 w-12 mx-auto mb-3 opacity-30" />
        <p className="text-sm font-medium">No Hover Job Linked</p>
        <p className="text-xs mt-1">Link a Hover job to view 3D models and design data</p>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {/* Header with Status and Actions */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <div className="p-2 rounded-lg bg-gradient-to-br from-blue-500 to-purple-600">
            <Box className="h-5 w-5 text-white" />
          </div>
          <div>
            <h3 className="font-semibold text-sm">Hover 3D Model</h3>
            <p className="text-xs text-slate-500">Job #{hoverJobId}</p>
          </div>
          <Badge className={statusInfo.color}>
            <StatusIcon className="h-3 w-3 mr-1" />
            {statusInfo.label}
          </Badge>
        </div>
        <div className="flex items-center gap-2">
          <Button variant="outline" size="sm" onClick={onRefresh}>
            <RefreshCw className="h-3 w-3 mr-1" />
            Sync
          </Button>
          <Button size="sm" onClick={openInHover}>
            <ExternalLink className="h-3 w-3 mr-1" />
            Open in Hover
          </Button>
        </div>
      </div>

      {/* Tabs for different views */}
      <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
        <TabsList className="grid w-full grid-cols-4">
          <TabsTrigger value="overview" className="text-xs">
            <Home className="h-3 w-3 mr-1" />
            Overview
          </TabsTrigger>
          <TabsTrigger value="photos" className="text-xs">
            <Image className="h-3 w-3 mr-1" />
            Photos ({photos.length})
          </TabsTrigger>
          <TabsTrigger value="measurements" className="text-xs">
            <Ruler className="h-3 w-3 mr-1" />
            Measurements
          </TabsTrigger>
          <TabsTrigger value="files" className="text-xs">
            <FileText className="h-3 w-3 mr-1" />
            Files ({deliverables.length})
          </TabsTrigger>
        </TabsList>

        {/* Overview Tab */}
        <TabsContent value="overview" className="mt-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {/* 3D Model Preview Card */}
            <Card className="overflow-hidden">
              <CardHeader className="pb-2">
                <CardTitle className="text-sm flex items-center gap-2">
                  <Box className="h-4 w-4 text-blue-500" />
                  3D Model Preview
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="relative aspect-video bg-gradient-to-br from-slate-100 to-slate-200 dark:from-slate-800 dark:to-slate-900 rounded-lg overflow-hidden">
                  {photos.length > 0 ? (
                    <img 
                      src={photos[0]?.url} 
                      alt="Property" 
                      className="w-full h-full object-cover"
                    />
                  ) : (
                    <div className="absolute inset-0 flex items-center justify-center">
                      <Building className="h-16 w-16 text-slate-300 dark:text-slate-600" />
                    </div>
                  )}
                  <div className="absolute inset-0 bg-gradient-to-t from-black/50 to-transparent" />
                  <div className="absolute bottom-3 left-3 right-3 flex justify-between items-end">
                    <div>
                      <p className="text-white text-xs opacity-80">Hover Job</p>
                      <p className="text-white font-semibold">#{hoverJobId}</p>
                    </div>
                    <Button size="sm" variant="secondary" onClick={openInHover}>
                      <Eye className="h-3 w-3 mr-1" />
                      View 3D
                    </Button>
                  </div>
                </div>
                {modelIds.length > 0 && (
                  <div className="mt-3 flex items-center gap-2 text-xs text-slate-500">
                    <Layers className="h-3 w-3" />
                    <span>{modelIds.length} model{modelIds.length !== 1 ? 's' : ''} available</span>
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Quick Stats Card */}
            <Card>
              <CardHeader className="pb-2">
                <CardTitle className="text-sm flex items-center gap-2">
                  <Calculator className="h-4 w-4 text-emerald-500" />
                  Quick Measurements
                </CardTitle>
              </CardHeader>
              <CardContent>
                {measurements ? (
                  <div className="grid grid-cols-2 gap-3">
                    {measurements.roof_area_sqft && (
                      <div className="p-3 rounded-lg bg-blue-50 dark:bg-blue-900/20">
                        <p className="text-xs text-blue-600 dark:text-blue-400 uppercase">Roof Area</p>
                        <p className="text-xl font-bold">{measurements.roof_area_sqft.toLocaleString()}</p>
                        <p className="text-xs text-slate-500">sqft</p>
                      </div>
                    )}
                    {measurements.wall_area_sqft && (
                      <div className="p-3 rounded-lg bg-purple-50 dark:bg-purple-900/20">
                        <p className="text-xs text-purple-600 dark:text-purple-400 uppercase">Wall Area</p>
                        <p className="text-xl font-bold">{measurements.wall_area_sqft.toLocaleString()}</p>
                        <p className="text-xs text-slate-500">sqft</p>
                      </div>
                    )}
                    {measurements.fascia_lf && (
                      <div className="p-3 rounded-lg bg-amber-50 dark:bg-amber-900/20">
                        <p className="text-xs text-amber-600 dark:text-amber-400 uppercase">Fascia</p>
                        <p className="text-xl font-bold">{measurements.fascia_lf.toLocaleString()}</p>
                        <p className="text-xs text-slate-500">LF</p>
                      </div>
                    )}
                    {measurements.soffit_sqft && (
                      <div className="p-3 rounded-lg bg-emerald-50 dark:bg-emerald-900/20">
                        <p className="text-xs text-emerald-600 dark:text-emerald-400 uppercase">Soffit</p>
                        <p className="text-xl font-bold">{measurements.soffit_sqft.toLocaleString()}</p>
                        <p className="text-xs text-slate-500">sqft</p>
                      </div>
                    )}
                  </div>
                ) : (
                  <div className="text-center py-6 text-slate-500">
                    <Calculator className="h-8 w-8 mx-auto mb-2 opacity-30" />
                    <p className="text-sm">Measurements pending</p>
                    <p className="text-xs">Sync when Hover processing is complete</p>
                  </div>
                )}
              </CardContent>
            </Card>
          </div>
        </TabsContent>

        {/* Photos Tab */}
        <TabsContent value="photos" className="mt-4">
          {photos.length > 0 ? (
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3">
              {photos.map((photo, idx) => (
                <div
                  key={photo.id || idx}
                  className="group relative aspect-square rounded-lg overflow-hidden cursor-pointer border border-slate-200 dark:border-slate-700 hover:border-blue-500 transition-all"
                  onClick={() => {
                    setSelectedPhoto(photo);
                    setPhotoIndex(idx);
                  }}
                >
                  <img
                    src={photo.url}
                    alt={photo.label || `Photo ${idx + 1}`}
                    className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                    loading="lazy"
                  />
                  <div className="absolute inset-0 bg-black/0 group-hover:bg-black/20 transition-colors" />
                  <div className="absolute bottom-0 left-0 right-0 p-2 bg-gradient-to-t from-black/60 to-transparent opacity-0 group-hover:opacity-100 transition-opacity">
                    <p className="text-white text-xs truncate">{photo.label || `Photo ${idx + 1}`}</p>
                  </div>
                  <div className="absolute top-2 right-2 opacity-0 group-hover:opacity-100 transition-opacity">
                    <Button size="icon" variant="secondary" className="h-6 w-6">
                      <Maximize2 className="h-3 w-3" />
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="text-center py-12 text-slate-500">
              <Image className="h-12 w-12 mx-auto mb-3 opacity-30" />
              <p className="text-sm font-medium">No Photos Available</p>
              <p className="text-xs mt-1">Photos will appear after Hover processes the job</p>
            </div>
          )}
        </TabsContent>

        {/* Measurements Tab */}
        <TabsContent value="measurements" className="mt-4">
          <HoverMeasurementsSummary measurements={measurements} />
        </TabsContent>

        {/* Files Tab */}
        <TabsContent value="files" className="mt-4">
          {deliverables.length > 0 ? (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              {deliverables.map((deliv, idx) => {
                const style = getDeliverableStyle(deliv.type);
                const DelivIcon = style.icon;
                return (
                  <div
                    key={deliv.id || idx}
                    className={`flex items-center justify-between p-4 rounded-lg border ${style.bg} border-slate-200 dark:border-slate-700`}
                  >
                    <div className="flex items-center gap-3">
                      <div className={`p-2 rounded-lg bg-white dark:bg-slate-800`}>
                        <DelivIcon className={`h-5 w-5 ${style.color}`} />
                      </div>
                      <div>
                        <p className="font-medium text-sm">{deliv.name || `${deliv.type?.toUpperCase()} Report`}</p>
                        <p className="text-xs text-slate-500">{deliv.type?.toUpperCase()} • {deliv.created_at ? new Date(deliv.created_at).toLocaleDateString() : 'N/A'}</p>
                      </div>
                    </div>
                    {deliv.url && (
                      <Button variant="ghost" size="sm" asChild>
                        <a href={deliv.url} target="_blank" rel="noopener noreferrer">
                          <Download className="h-4 w-4" />
                        </a>
                      </Button>
                    )}
                  </div>
                );
              })}
            </div>
          ) : (
            <div className="text-center py-12 text-slate-500">
              <FileText className="h-12 w-12 mx-auto mb-3 opacity-30" />
              <p className="text-sm font-medium">No Files Available</p>
              <p className="text-xs mt-1">Deliverables (PDF, Excel, SketchUp) will appear after processing</p>
            </div>
          )}
        </TabsContent>
      </Tabs>

      {/* Photo Lightbox Modal */}
      <Dialog open={!!selectedPhoto} onOpenChange={() => setSelectedPhoto(null)}>
        <DialogContent className="max-w-5xl p-0 overflow-hidden">
          <div className="relative">
            <img
              src={selectedPhoto?.url}
              alt={selectedPhoto?.label || 'Photo'}
              className="w-full max-h-[80vh] object-contain bg-black"
            />
            {/* Navigation */}
            {photos.length > 1 && (
              <>
                <button
                  onClick={(e) => { e.stopPropagation(); prevPhoto(); }}
                  className="absolute left-4 top-1/2 -translate-y-1/2 p-2 rounded-full bg-black/50 text-white hover:bg-black/70 transition-colors"
                >
                  <ChevronDown className="h-6 w-6 rotate-90" />
                </button>
                <button
                  onClick={(e) => { e.stopPropagation(); nextPhoto(); }}
                  className="absolute right-4 top-1/2 -translate-y-1/2 p-2 rounded-full bg-black/50 text-white hover:bg-black/70 transition-colors"
                >
                  <ChevronDown className="h-6 w-6 -rotate-90" />
                </button>
              </>
            )}
            {/* Info bar */}
            <div className="absolute bottom-0 left-0 right-0 p-4 bg-gradient-to-t from-black/80 to-transparent">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-white font-medium">{selectedPhoto?.label || `Photo ${photoIndex + 1}`}</p>
                  <p className="text-white/70 text-sm">{photoIndex + 1} of {photos.length}</p>
                </div>
                <div className="flex items-center gap-2">
                  <Button variant="secondary" size="sm" asChild>
                    <a href={selectedPhoto?.url} target="_blank" rel="noopener noreferrer">
                      <ExternalLink className="h-4 w-4 mr-1" />
                      Open
                    </a>
                  </Button>
                  <Button variant="secondary" size="sm" asChild>
                    <a href={selectedPhoto?.url} download>
                      <Download className="h-4 w-4 mr-1" />
                      Download
                    </a>
                  </Button>
                </div>
              </div>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

// ============================================================================
// SUMMARY CARDS COMPONENT
// ============================================================================

const SummaryCard = ({ title, value, unit, icon: Icon, variant = 'default' }) => {
  const variantStyles = {
    default: 'bg-white dark:bg-slate-800 border-slate-200 dark:border-slate-700',
    coating: 'bg-blue-50 dark:bg-blue-900/20 border-blue-200 dark:border-blue-800',
    repair: 'bg-amber-50 dark:bg-amber-900/20 border-amber-200 dark:border-amber-800',
  };

  return (
    <div className={`rounded-lg border p-4 ${variantStyles[variant]}`}>
      <div className="flex items-center justify-between">
        <div>
          <p className="text-xs font-medium text-slate-500 dark:text-slate-400 uppercase tracking-wider">
            {title}
          </p>
          <p className="text-2xl font-bold mt-1">
            {typeof value === 'number' ? value.toLocaleString(undefined, { maximumFractionDigits: 2 }) : value}
            <span className="text-sm font-normal text-slate-500 ml-1">{unit}</span>
          </p>
        </div>
        {Icon && <Icon className="h-8 w-8 text-slate-300 dark:text-slate-600" />}
      </div>
    </div>
  );
};

// ============================================================================
// MEASUREMENT LINE ITEM COMPONENT
// ============================================================================

const MeasurementLineItem = ({ 
  item, 
  onUpdate, 
  onDelete, 
  onDuplicate, 
  disabled,
  category 
}) => {
  const [isEditing, setIsEditing] = useState(false);
  const [localItem, setLocalItem] = useState(item);

  useEffect(() => {
    setLocalItem(item);
  }, [item]);

  const handleChange = (field, value) => {
    setLocalItem(prev => ({ ...prev, [field]: value }));
  };

  const handleSave = () => {
    onUpdate(item.id, localItem);
    setIsEditing(false);
  };

  const handleCancel = () => {
    setLocalItem(item);
    setIsEditing(false);
  };

  // Calculate display value
  const displayValue = useMemo(() => {
    if (item.measurement_type === 'AREA') {
      return { value: item.calculated_sqft || 0, unit: 'sqft' };
    } else if (item.measurement_type === 'LINEAR') {
      return { value: item.calculated_lf || 0, unit: 'LF' };
    } else {
      return { value: item.count_each || 0, unit: 'ea' };
    }
  }, [item]);

  const isIncomplete = !item.name || 
    (item.measurement_type === 'AREA' && (!item.height_ft || !item.width_ft)) ||
    (item.measurement_type === 'LINEAR' && !item.length_ft) ||
    (item.measurement_type === 'COUNT' && !item.count_each);

  if (isEditing) {
    return (
      <div className="bg-slate-50 dark:bg-slate-800/50 rounded-lg p-4 border border-blue-200 dark:border-blue-800">
        <div className="grid grid-cols-12 gap-3">
          {/* Name */}
          <div className="col-span-3">
            <Label className="text-xs">Item Name</Label>
            <Input
              value={localItem.name || ''}
              onChange={(e) => handleChange('name', e.target.value)}
              placeholder="e.g., Front Wall Section A"
              className="h-8 text-sm"
            />
          </div>

          {/* Measurement Type */}
          <div className="col-span-2">
            <Label className="text-xs">Type</Label>
            <Select
              value={localItem.measurement_type}
              onValueChange={(val) => handleChange('measurement_type', val)}
            >
              <SelectTrigger className="h-8 text-sm">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="AREA">Area (SqFt)</SelectItem>
                <SelectItem value="LINEAR">Linear (LF)</SelectItem>
                <SelectItem value="COUNT">Count (ea)</SelectItem>
              </SelectContent>
            </Select>
          </div>

          {/* Dimensions based on type */}
          {localItem.measurement_type === 'AREA' && (
            <>
              <div className="col-span-1">
                <Label className="text-xs">Height (ft)</Label>
                <Input
                  type="number"
                  value={localItem.height_ft || ''}
                  onChange={(e) => handleChange('height_ft', parseFloat(e.target.value) || null)}
                  className="h-8 text-sm"
                  step="0.1"
                />
              </div>
              <div className="col-span-1">
                <Label className="text-xs">Width (ft)</Label>
                <Input
                  type="number"
                  value={localItem.width_ft || ''}
                  onChange={(e) => handleChange('width_ft', parseFloat(e.target.value) || null)}
                  className="h-8 text-sm"
                  step="0.1"
                />
              </div>
              <div className="col-span-1">
                <Label className="text-xs">Qty</Label>
                <Input
                  type="number"
                  value={localItem.quantity || 1}
                  onChange={(e) => handleChange('quantity', parseInt(e.target.value) || 1)}
                  className="h-8 text-sm"
                  min="1"
                />
              </div>
            </>
          )}

          {localItem.measurement_type === 'LINEAR' && (
            <>
              <div className="col-span-2">
                <Label className="text-xs">Length (ft)</Label>
                <Input
                  type="number"
                  value={localItem.length_ft || ''}
                  onChange={(e) => handleChange('length_ft', parseFloat(e.target.value) || null)}
                  className="h-8 text-sm"
                  step="0.1"
                />
              </div>
              <div className="col-span-1">
                <Label className="text-xs">Qty</Label>
                <Input
                  type="number"
                  value={localItem.quantity || 1}
                  onChange={(e) => handleChange('quantity', parseInt(e.target.value) || 1)}
                  className="h-8 text-sm"
                  min="1"
                />
              </div>
            </>
          )}

          {localItem.measurement_type === 'COUNT' && (
            <div className="col-span-2">
              <Label className="text-xs">Count</Label>
              <Input
                type="number"
                value={localItem.count_each || ''}
                onChange={(e) => handleChange('count_each', parseInt(e.target.value) || null)}
                className="h-8 text-sm"
                min="1"
              />
            </div>
          )}

          {/* Severity (repairs only) */}
          {category === 'REPAIR' && (
            <div className="col-span-2">
              <Label className="text-xs">Severity</Label>
              <Select
                value={localItem.severity || ''}
                onValueChange={(val) => handleChange('severity', val)}
              >
                <SelectTrigger className="h-8 text-sm">
                  <SelectValue placeholder="Select..." />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="Minor">Minor</SelectItem>
                  <SelectItem value="Moderate">Moderate</SelectItem>
                  <SelectItem value="Major">Major</SelectItem>
                </SelectContent>
              </Select>
            </div>
          )}

          {/* Include in totals */}
          <div className="col-span-1 flex items-end pb-1">
            <div className="flex items-center gap-2">
              <Switch
                checked={localItem.include_in_totals !== false}
                onCheckedChange={(val) => handleChange('include_in_totals', val)}
              />
              <Label className="text-xs">Include</Label>
            </div>
          </div>
        </div>

        {/* Notes */}
        <div className="mt-3">
          <Label className="text-xs">Notes</Label>
          <Input
            value={localItem.notes || ''}
            onChange={(e) => handleChange('notes', e.target.value)}
            placeholder="Optional notes..."
            className="h-8 text-sm"
          />
        </div>

        {/* Actions */}
        <div className="flex justify-end gap-2 mt-3">
          <Button variant="outline" size="sm" onClick={handleCancel}>
            Cancel
          </Button>
          <Button size="sm" onClick={handleSave}>
            Save
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className={`grid grid-cols-12 gap-2 items-center py-2 px-3 rounded-lg hover:bg-slate-50 dark:hover:bg-slate-800/50 ${
      isIncomplete ? 'bg-amber-50/50 dark:bg-amber-900/10' : ''
    }`}>
      {/* Name */}
      <div className="col-span-2 flex items-center gap-2">
        {isIncomplete && <AlertTriangle className="h-3 w-3 text-amber-500" />}
        <span className="text-sm font-medium truncate">{item.name || 'Unnamed'}</span>
      </div>

      {/* Type */}
      <div className="col-span-1">
        <Badge variant="outline" className="text-xs">
          {item.measurement_type === 'AREA' ? 'Area' : 
           item.measurement_type === 'LINEAR' ? 'Linear' : 'Count'}
        </Badge>
      </div>

      {/* Dimensions */}
      <div className="col-span-2 text-sm text-slate-600 dark:text-slate-400">
        {item.measurement_type === 'AREA' && (
          <span>{item.height_ft || '-'} × {item.width_ft || '-'} × {item.quantity || 1}</span>
        )}
        {item.measurement_type === 'LINEAR' && (
          <span>{item.length_ft || '-'} ft × {item.quantity || 1}</span>
        )}
        {item.measurement_type === 'COUNT' && (
          <span>{item.count_each || '-'} ea</span>
        )}
      </div>

      {/* Calculated Value (Hard Measure) */}
      <div className="col-span-2 text-right font-medium">
        <span className="text-slate-900 dark:text-slate-100">
          {displayValue.value.toLocaleString(undefined, { maximumFractionDigits: 2 })} {displayValue.unit}
        </span>
      </div>

      {/* Hover Value (Material Ordering) */}
      <div className="col-span-2 text-right">
        {(item.hover_sqft || item.hover_lf) ? (
          <span className="font-medium text-blue-600 dark:text-blue-400">
            {item.measurement_type === 'AREA' && item.hover_sqft
              ? `${item.hover_sqft.toLocaleString(undefined, { maximumFractionDigits: 2 })} sqft`
              : item.hover_lf
                ? `${item.hover_lf.toLocaleString(undefined, { maximumFractionDigits: 2 })} LF`
                : '—'
            }
          </span>
        ) : (
          <span className="text-slate-400">—</span>
        )}
      </div>

      {/* Severity (repairs) */}
      {category === 'REPAIR' ? (
        <div className="col-span-1">
          {item.severity && (
            <Badge variant={
              item.severity === 'Major' ? 'destructive' :
              item.severity === 'Moderate' ? 'warning' : 'secondary'
            } className="text-xs">
              {item.severity}
            </Badge>
          )}
        </div>
      ) : (
        <div className="col-span-1" />
      )}

      {/* Actions */}
      <div className="col-span-2 flex justify-end gap-1">
        <Button 
          variant="ghost" 
          size="sm" 
          className="h-7 w-7 p-0"
          onClick={() => setIsEditing(true)}
          disabled={disabled}
        >
          <Edit2 className="h-3 w-3" />
        </Button>
        <Button 
          variant="ghost" 
          size="sm" 
          className="h-7 w-7 p-0"
          onClick={() => onDuplicate(item.id)}
          disabled={disabled}
        >
          <Copy className="h-3 w-3" />
        </Button>
        <AlertDialog>
          <AlertDialogTrigger asChild>
            <Button 
              variant="ghost" 
              size="sm" 
              className="h-7 w-7 p-0 text-red-500 hover:text-red-600"
              disabled={disabled}
            >
              <Trash2 className="h-3 w-3" />
            </Button>
          </AlertDialogTrigger>
          <AlertDialogContent>
            <AlertDialogHeader>
              <AlertDialogTitle>Delete Measurement</AlertDialogTitle>
              <AlertDialogDescription>
                Are you sure you want to delete &quot;{item.name}&quot;? This action cannot be undone.
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel>Cancel</AlertDialogCancel>
              <AlertDialogAction onClick={() => onDelete(item.id)} className="bg-red-500 hover:bg-red-600">
                Delete
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
      </div>
    </div>
  );
};

// ============================================================================
// MEASUREMENT GROUP COMPONENT
// ============================================================================

const MeasurementGroup = ({ 
  group, 
  category,
  onAddItem, 
  onUpdateItem, 
  onDeleteItem, 
  onDuplicateItem,
  disabled 
}) => {
  const [isExpanded, setIsExpanded] = useState(group.items.length > 0);
  const [isAddingItem, setIsAddingItem] = useState(false);
  const [newItem, setNewItem] = useState({
    name: '',
    measurement_type: 'AREA',
    height_ft: null,
    width_ft: null,
    length_ft: null,
    quantity: 1,
    count_each: null,
    severity: null,
    notes: '',
    include_in_totals: true
  });

  const handleAddItem = () => {
    if (!newItem.name) {
      toast.error('Please enter an item name');
      return;
    }
    onAddItem(group.key, newItem);
    setNewItem({
      name: '',
      measurement_type: 'AREA',
      height_ft: null,
      width_ft: null,
      length_ft: null,
      quantity: 1,
      count_each: null,
      severity: null,
      notes: '',
      include_in_totals: true
    });
    setIsAddingItem(false);
  };

  const hasItems = group.items.length > 0;
  const hasSubtotals = group.sqft > 0 || group.lf > 0 || group.count > 0;

  return (
    <div className="border rounded-lg overflow-hidden bg-white dark:bg-slate-900">
      {/* Group Header */}
      <div 
        className={`flex items-center justify-between px-4 py-3 cursor-pointer hover:bg-slate-50 dark:hover:bg-slate-800 ${
          category === 'COATING' ? 'bg-blue-50/50 dark:bg-blue-900/10' : 'bg-amber-50/50 dark:bg-amber-900/10'
        }`}
        onClick={() => setIsExpanded(!isExpanded)}
      >
        <div className="flex items-center gap-3">
          {isExpanded ? (
            <ChevronDown className="h-4 w-4 text-slate-500" />
          ) : (
            <ChevronRight className="h-4 w-4 text-slate-500" />
          )}
          <span className="font-medium">{group.label}</span>
          <Badge variant="outline" className="text-xs">
            {group.items.length} item{group.items.length !== 1 ? 's' : ''}
          </Badge>
        </div>

        {/* Subtotals */}
        {hasSubtotals && (
          <div className="flex items-center gap-4 text-sm">
            <span className="text-slate-600 dark:text-slate-400">
              Hard: <strong>{group.sqft > 0 ? `${group.sqft.toLocaleString()} sqft` : ''}</strong>
              {group.lf > 0 && <strong> {group.lf.toLocaleString()} LF</strong>}
            </span>
            {(group.hover_sqft > 0 || group.hover_lf > 0) && (
              <span className="text-blue-600 dark:text-blue-400">
                Hover: <strong>{group.hover_sqft > 0 ? `${group.hover_sqft.toLocaleString()} sqft` : ''}</strong>
                {group.hover_lf > 0 && <strong> {group.hover_lf.toLocaleString()} LF</strong>}
              </span>
            )}
          </div>
        )}
      </div>

      {/* Group Content */}
      {isExpanded && (
        <div className="border-t dark:border-slate-700">
          {/* Column Headers */}
          {hasItems && (
            <div className="grid grid-cols-12 gap-2 px-3 py-2 text-xs font-medium text-slate-500 uppercase tracking-wider bg-slate-50 dark:bg-slate-800/50">
              <div className="col-span-2">Item</div>
              <div className="col-span-1">Type</div>
              <div className="col-span-2">Dimensions</div>
              <div className="col-span-2 text-right">Calculated</div>
              <div className="col-span-2 text-right text-blue-600">Hover</div>
              <div className="col-span-1">{category === 'REPAIR' ? 'Severity' : ''}</div>
              <div className="col-span-2 text-right">Actions</div>
            </div>
          )}

          {/* Items */}
          <div className="divide-y dark:divide-slate-700">
            {group.items.map((item) => (
              <MeasurementLineItem
                key={item.id}
                item={item}
                category={category}
                onUpdate={onUpdateItem}
                onDelete={onDeleteItem}
                onDuplicate={onDuplicateItem}
                disabled={disabled}
              />
            ))}
          </div>

          {/* Add Item Form */}
          {isAddingItem && (
            <div className="p-4 bg-slate-50 dark:bg-slate-800/50 border-t dark:border-slate-700">
              <div className="grid grid-cols-12 gap-3">
                <div className="col-span-3">
                  <Label className="text-xs">Item Name</Label>
                  <Input
                    value={newItem.name}
                    onChange={(e) => setNewItem(prev => ({ ...prev, name: e.target.value }))}
                    placeholder="e.g., Wall Section A"
                    className="h-8 text-sm"
                    autoFocus
                  />
                </div>
                <div className="col-span-2">
                  <Label className="text-xs">Type</Label>
                  <Select
                    value={newItem.measurement_type}
                    onValueChange={(val) => setNewItem(prev => ({ ...prev, measurement_type: val }))}
                  >
                    <SelectTrigger className="h-8 text-sm">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="AREA">Area (SqFt)</SelectItem>
                      <SelectItem value="LINEAR">Linear (LF)</SelectItem>
                      <SelectItem value="COUNT">Count (ea)</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                {newItem.measurement_type === 'AREA' && (
                  <>
                    <div className="col-span-1">
                      <Label className="text-xs">H (ft)</Label>
                      <Input
                        type="number"
                        value={newItem.height_ft || ''}
                        onChange={(e) => setNewItem(prev => ({ ...prev, height_ft: parseFloat(e.target.value) || null }))}
                        className="h-8 text-sm"
                        step="0.1"
                      />
                    </div>
                    <div className="col-span-1">
                      <Label className="text-xs">W (ft)</Label>
                      <Input
                        type="number"
                        value={newItem.width_ft || ''}
                        onChange={(e) => setNewItem(prev => ({ ...prev, width_ft: parseFloat(e.target.value) || null }))}
                        className="h-8 text-sm"
                        step="0.1"
                      />
                    </div>
                    <div className="col-span-1">
                      <Label className="text-xs">Qty</Label>
                      <Input
                        type="number"
                        value={newItem.quantity || 1}
                        onChange={(e) => setNewItem(prev => ({ ...prev, quantity: parseInt(e.target.value) || 1 }))}
                        className="h-8 text-sm"
                        min="1"
                      />
                    </div>
                  </>
                )}

                {newItem.measurement_type === 'LINEAR' && (
                  <>
                    <div className="col-span-2">
                      <Label className="text-xs">Length (ft)</Label>
                      <Input
                        type="number"
                        value={newItem.length_ft || ''}
                        onChange={(e) => setNewItem(prev => ({ ...prev, length_ft: parseFloat(e.target.value) || null }))}
                        className="h-8 text-sm"
                        step="0.1"
                      />
                    </div>
                    <div className="col-span-1">
                      <Label className="text-xs">Qty</Label>
                      <Input
                        type="number"
                        value={newItem.quantity || 1}
                        onChange={(e) => setNewItem(prev => ({ ...prev, quantity: parseInt(e.target.value) || 1 }))}
                        className="h-8 text-sm"
                        min="1"
                      />
                    </div>
                  </>
                )}

                {newItem.measurement_type === 'COUNT' && (
                  <div className="col-span-2">
                    <Label className="text-xs">Count</Label>
                    <Input
                      type="number"
                      value={newItem.count_each || ''}
                      onChange={(e) => setNewItem(prev => ({ ...prev, count_each: parseInt(e.target.value) || null }))}
                      className="h-8 text-sm"
                      min="1"
                    />
                  </div>
                )}

                {category === 'REPAIR' && (
                  <div className="col-span-2">
                    <Label className="text-xs">Severity</Label>
                    <Select
                      value={newItem.severity || ''}
                      onValueChange={(val) => setNewItem(prev => ({ ...prev, severity: val }))}
                    >
                      <SelectTrigger className="h-8 text-sm">
                        <SelectValue placeholder="Select..." />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="Minor">Minor</SelectItem>
                        <SelectItem value="Moderate">Moderate</SelectItem>
                        <SelectItem value="Major">Major</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                )}
              </div>

              <div className="flex justify-end gap-2 mt-3">
                <Button variant="outline" size="sm" onClick={() => setIsAddingItem(false)}>
                  Cancel
                </Button>
                <Button size="sm" onClick={handleAddItem}>
                  Add Item
                </Button>
              </div>
            </div>
          )}

          {/* Empty State / Add Button */}
          {!isAddingItem && (
            <div className="p-3 border-t dark:border-slate-700">
              {!hasItems && (
                <p className="text-sm text-slate-500 mb-3 text-center">
                  No measurements added yet
                </p>
              )}
              <Button
                variant="outline"
                size="sm"
                className="w-full"
                onClick={() => setIsAddingItem(true)}
                disabled={disabled}
              >
                <Plus className="h-3 w-3 mr-2" />
                Add Measurement
              </Button>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

// ============================================================================
// MAIN MEASUREMENTS TAB COMPONENT
// ============================================================================

export const MeasurementsTab = ({ project, user, onRefresh }) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  // Hover Integration State
  const [hoverStatus, setHoverStatus] = useState(null);
  const [hoverData, setHoverData] = useState(null);
  const [hoverLoading, setHoverLoading] = useState(false);
  const [hoverSyncing, setHoverSyncing] = useState(false);
  const [linkDialogOpen, setLinkDialogOpen] = useState(false);
  const [webhookStatus, setWebhookStatus] = useState(null);

  // Check permissions
  const canEdit = useMemo(() => {
    const role = user?.role || '';
    return ['super_admin', 'admin', 'sales', 'sales_rep', 'sales_manager', 
            'director_of_sales', 'ops', 'operations', 'ops_manager', 'operations_manager',
            'foreman', 'project_manager'].includes(role);
  }, [user?.role]);

  const isAdmin = useMemo(() => {
    const role = user?.role || '';
    return ['super_admin', 'admin'].includes(role);
  }, [user?.role]);

  const canLinkHover = useMemo(() => {
    const role = user?.role || '';
    return ['super_admin', 'admin', 'operations_manager', 'ops_manager', 
            'production_manager', 'general_manager', 'director_of_sales'].includes(role);
  }, [user?.role]);

  // File upload ref
  const fileInputRef = useRef(null);
  const [uploading, setUploading] = useState(false);

  // Fetch Hover connection status (includes webhook status)
  const fetchHoverStatus = useCallback(async () => {
    try {
      const [statusRes, webhookRes] = await Promise.all([
        axios.get(`${API}/integrations/hover/status`),
        axios.get(`${API}/integrations/hover/webhooks/status`).catch(() => ({ data: { registered: false } }))
      ]);
      setHoverStatus(statusRes.data);
      setWebhookStatus(webhookRes.data);
    } catch (error) {
      console.error('Error fetching Hover status:', error);
      setHoverStatus({ connected: false, error: 'Failed to check status' });
      setWebhookStatus({ registered: false });
    }
  }, []);

  // Fetch Hover data for this job
  const fetchHoverData = useCallback(async () => {
    if (!project?.id) return;
    
    setHoverLoading(true);
    try {
      const response = await axios.get(`${API}/integrations/hover/jobs/${project.id}/hover-data`);
      setHoverData(response.data);
    } catch (error) {
      console.error('Error fetching Hover data:', error);
      setHoverData(null);
    } finally {
      setHoverLoading(false);
    }
  }, [project?.id]);

  // Connect to Hover (opens OAuth flow)
  const handleConnectHover = useCallback(() => {
    const width = 600;
    const height = 700;
    const left = window.screenX + (window.outerWidth - width) / 2;
    const top = window.screenY + (window.outerHeight - height) / 2;
    
    const popup = window.open(
      `${API}/integrations/hover/connect`,
      'hover-oauth',
      `width=${width},height=${height},left=${left},top=${top}`
    );

    // Listen for OAuth completion
    const handleMessage = (event) => {
      if (event.data?.type === 'HOVER_CONNECTED') {
        popup?.close();
        fetchHoverStatus();
        toast.success('Hover connected successfully!');
      }
    };
    window.addEventListener('message', handleMessage);

    // Cleanup
    const checkClosed = setInterval(() => {
      if (popup?.closed) {
        clearInterval(checkClosed);
        window.removeEventListener('message', handleMessage);
        fetchHoverStatus();
      }
    }, 500);
  }, [fetchHoverStatus]);

  // Link Hover job
  const handleLinkHoverJob = useCallback(async (hoverJobId) => {
    if (!project?.id) return;
    
    setHoverSyncing(true);
    try {
      await axios.post(`${API}/integrations/hover/jobs/${project.id}/link`, {
        hover_job_id: hoverJobId,
        sync_immediately: true
      });
      toast.success('Hover job linked and synced!');
      setLinkDialogOpen(false);
      await fetchHoverData();
    } catch (error) {
      console.error('Error linking Hover job:', error);
      toast.error(error.response?.data?.detail || 'Failed to link Hover job');
    } finally {
      setHoverSyncing(false);
    }
  }, [project?.id, fetchHoverData]);

  // Sync Hover data
  const handleSyncHover = useCallback(async () => {
    if (!project?.id || !hoverData?.hover_job_id) return;
    
    setHoverSyncing(true);
    try {
      const response = await axios.post(`${API}/integrations/hover/jobs/${project.id}/sync`);
      toast.success(`Synced: ${response.data.photos_count} photos, ${response.data.deliverables_count} deliverables`);
      await fetchHoverData();
    } catch (error) {
      console.error('Error syncing Hover:', error);
      toast.error(error.response?.data?.detail || 'Failed to sync Hover data');
    } finally {
      setHoverSyncing(false);
    }
  }, [project?.id, hoverData?.hover_job_id, fetchHoverData]);

  // Unlink Hover job
  const handleUnlinkHover = useCallback(async () => {
    if (!project?.id) return;
    
    try {
      await axios.delete(`${API}/integrations/hover/jobs/${project.id}/link`);
      toast.success('Hover job unlinked');
      setHoverData(null);
    } catch (error) {
      console.error('Error unlinking Hover:', error);
      toast.error('Failed to unlink Hover job');
    }
  }, [project?.id]);

  // Fetch measurements
  const fetchMeasurements = useCallback(async () => {
    if (!project?.id) return;
    
    try {
      setLoading(true);
      const response = await axios.get(`${API}/jobs/${project.id}/measurements`);
      setData(response.data);
    } catch (error) {
      console.error('Error fetching measurements:', error);
      toast.error('Failed to load measurements');
    } finally {
      setLoading(false);
    }
  }, [project?.id]);

  useEffect(() => {
    fetchMeasurements();
    fetchHoverStatus();
    fetchHoverData();
  }, [fetchMeasurements, fetchHoverStatus, fetchHoverData]);

  // Handle Hover CSV upload
  const handleHoverUpload = useCallback(async (event) => {
    const file = event.target.files?.[0];
    if (!file) return;
    
    if (!file.name.toLowerCase().endsWith('.csv')) {
      toast.error('Please upload a CSV file');
      return;
    }
    
    setUploading(true);
    try {
      const formData = new FormData();
      formData.append('file', file);
      
      const response = await axios.post(
        `${API}/jobs/${project.id}/measurements/hover-upload`,
        formData,
        { headers: { 'Content-Type': 'multipart/form-data' } }
      );
      
      toast.success(`Hover report uploaded: ${response.data.areas_mapped} areas mapped, ${response.data.total_sqft.toLocaleString()} sqft`);
      await fetchMeasurements();
    } catch (error) {
      console.error('Error uploading Hover CSV:', error);
      toast.error(error.response?.data?.detail || 'Failed to upload Hover CSV');
    } finally {
      setUploading(false);
      // Reset file input
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    }
  }, [project?.id, fetchMeasurements]);

  // Delete Hover report
  const handleDeleteHoverReport = useCallback(async () => {
    if (!project?.id) return;
    
    try {
      await axios.delete(`${API}/jobs/${project.id}/measurements/hover-report`);
      toast.success('Hover report deleted');
      await fetchMeasurements();
    } catch (error) {
      console.error('Error deleting Hover report:', error);
      toast.error('Failed to delete Hover report');
    }
  }, [project?.id, fetchMeasurements]);

  // Add item
  const handleAddItem = useCallback(async (groupKey, itemData, category) => {
    if (!project?.id) return;
    
    setSaving(true);
    try {
      const payload = {
        ...itemData,
        category,
        ...(category === 'COATING' ? { area_key: groupKey } : { repair_group_key: groupKey })
      };
      
      await axios.post(`${API}/jobs/${project.id}/measurements/items`, payload);
      toast.success('Measurement added');
      await fetchMeasurements();
    } catch (error) {
      console.error('Error adding measurement:', error);
      toast.error('Failed to add measurement');
    } finally {
      setSaving(false);
    }
  }, [project?.id, fetchMeasurements]);

  // Update item
  const handleUpdateItem = useCallback(async (itemId, itemData) => {
    if (!project?.id) return;
    
    setSaving(true);
    try {
      await axios.put(`${API}/jobs/${project.id}/measurements/items/${itemId}`, itemData);
      toast.success('Measurement updated');
      await fetchMeasurements();
    } catch (error) {
      console.error('Error updating measurement:', error);
      toast.error('Failed to update measurement');
    } finally {
      setSaving(false);
    }
  }, [project?.id, fetchMeasurements]);

  // Delete item
  const handleDeleteItem = useCallback(async (itemId) => {
    if (!project?.id) return;
    
    setSaving(true);
    try {
      await axios.delete(`${API}/jobs/${project.id}/measurements/items/${itemId}`);
      toast.success('Measurement deleted');
      await fetchMeasurements();
    } catch (error) {
      console.error('Error deleting measurement:', error);
      toast.error('Failed to delete measurement');
    } finally {
      setSaving(false);
    }
  }, [project?.id, fetchMeasurements]);

  // Duplicate item
  const handleDuplicateItem = useCallback(async (itemId) => {
    if (!project?.id) return;
    
    setSaving(true);
    try {
      await axios.post(`${API}/jobs/${project.id}/measurements/items/${itemId}/duplicate`);
      toast.success('Measurement duplicated');
      await fetchMeasurements();
    } catch (error) {
      console.error('Error duplicating measurement:', error);
      toast.error('Failed to duplicate measurement');
    } finally {
      setSaving(false);
    }
  }, [project?.id, fetchMeasurements]);

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="h-8 w-8 animate-spin text-slate-400" />
      </div>
    );
  }

  if (!data) {
    return (
      <div className="text-center py-12 text-slate-500">
        Failed to load measurements
      </div>
    );
  }

  const { coating_totals, repair_totals, hover_coating_totals, hover_repair_totals, coating_groups, repair_groups, hover_report, updated_at, updated_by } = data;

  return (
    <div className="space-y-6">
      {/* Hover API Integration Section */}
      <Card className="border-purple-200 dark:border-purple-800 bg-purple-50/30 dark:bg-purple-900/10">
        <CardHeader className="pb-3">
          <div className="flex items-center justify-between flex-wrap gap-4">
            <div className="flex items-center gap-3">
              <div className="p-2 rounded-lg bg-purple-100 dark:bg-purple-900/30">
                <Image className="h-5 w-5 text-purple-600 dark:text-purple-400" />
              </div>
              <div>
                <CardTitle className="text-base flex items-center gap-2">
                  Hover Integration
                  {hoverData?.hover_job_id && (
                    <Badge variant="outline" className="text-purple-600 border-purple-300">
                      Job #{hoverData.hover_job_id}
                    </Badge>
                  )}
                  {hoverData?.hover_status && (
                    <Badge className={
                      hoverData.hover_status === 'complete' 
                        ? 'bg-emerald-100 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-400'
                        : 'bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-400'
                    }>
                      {hoverData.hover_status}
                    </Badge>
                  )}
                </CardTitle>
                <CardDescription className="text-xs">
                  {hoverData?.hover_job_id 
                    ? `Last synced: ${hoverData.hover_last_sync_at ? new Date(hoverData.hover_last_sync_at).toLocaleString() : 'Never'}`
                    : 'Link a Hover job to pull measurements, photos, and deliverables'
                  }
                </CardDescription>
              </div>
            </div>
            
            <div className="flex items-center gap-2">
              <HoverConnectionStatus 
                status={hoverStatus} 
                onConnect={handleConnectHover}
                onRefresh={() => fetchHoverStatus()}
                isAdmin={isAdmin}
                webhookStatus={webhookStatus}
              />
              
              {hoverStatus?.connected && !hoverData?.hover_job_id && canLinkHover && (
                <Button 
                  variant="outline" 
                  size="sm"
                  onClick={() => setLinkDialogOpen(true)}
                >
                  <Link2 className="h-3 w-3 mr-1" />
                  Link Hover Job
                </Button>
              )}
              
              {hoverData?.hover_job_id && (
                <>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={handleSyncHover}
                    disabled={hoverSyncing || !hoverStatus?.connected}
                  >
                    {hoverSyncing ? (
                      <Loader2 className="h-3 w-3 mr-1 animate-spin" />
                    ) : (
                      <RefreshCw className="h-3 w-3 mr-1" />
                    )}
                    Sync Now
                  </Button>
                  
                  {canLinkHover && (
                    <AlertDialog>
                      <AlertDialogTrigger asChild>
                        <Button variant="ghost" size="sm" className="text-slate-500 hover:text-red-500">
                          <X className="h-4 w-4" />
                        </Button>
                      </AlertDialogTrigger>
                      <AlertDialogContent>
                        <AlertDialogHeader>
                          <AlertDialogTitle>Unlink Hover Job</AlertDialogTitle>
                          <AlertDialogDescription>
                            This will remove the Hover link but keep existing measurement data. Are you sure?
                          </AlertDialogDescription>
                        </AlertDialogHeader>
                        <AlertDialogFooter>
                          <AlertDialogCancel>Cancel</AlertDialogCancel>
                          <AlertDialogAction onClick={handleUnlinkHover}>
                            Unlink
                          </AlertDialogAction>
                        </AlertDialogFooter>
                      </AlertDialogContent>
                    </AlertDialog>
                  )}
                </>
              )}
            </div>
          </div>
        </CardHeader>
        
        {/* Hover Content - 3D Design Viewer */}
        {hoverData?.hover_job_id && (
          <CardContent>
            <HoverDesignViewer
              hoverJobId={hoverData.hover_job_id}
              hoverStatus={hoverData.hover_status}
              modelIds={hoverData.hover_model_ids || []}
              photos={hoverData.hover_photos || []}
              deliverables={hoverData.hover_deliverables || []}
              measurements={hoverData.hover_measurements}
              onRefresh={handleSyncHover}
            />
          </CardContent>
        )}
      </Card>

      {/* Link Hover Job Dialog */}
      <HoverJobLinkDialog
        open={linkDialogOpen}
        onOpenChange={setLinkDialogOpen}
        onLink={handleLinkHoverJob}
        loading={hoverSyncing}
      />

      {/* Hover CSV Upload Section */}
      <Card className="border-blue-200 dark:border-blue-800 bg-blue-50/30 dark:bg-blue-900/10">
        <CardHeader className="pb-3">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="p-2 rounded-lg bg-blue-100 dark:bg-blue-900/30">
                <FileSpreadsheet className="h-5 w-5 text-blue-600 dark:text-blue-400" />
              </div>
              <div>
                <CardTitle className="text-base">Hover CSV Report (Manual)</CardTitle>
                <CardDescription className="text-xs">
                  Upload Hover CSV for material ordering measurements (alternative to API sync)
                </CardDescription>
              </div>
            </div>
            
            <div className="flex items-center gap-2">
              {hover_report ? (
                <>
                  <Badge variant="outline" className="text-blue-600 border-blue-300">
                    {hover_report.filename}
                  </Badge>
                  <span className="text-xs text-slate-500">
                    {hover_report.total_sqft?.toLocaleString()} sqft | {hover_report.areas_mapped} areas
                  </span>
                  <AlertDialog>
                    <AlertDialogTrigger asChild>
                      <Button variant="ghost" size="sm" className="text-red-500 hover:text-red-600">
                        <X className="h-4 w-4" />
                      </Button>
                    </AlertDialogTrigger>
                    <AlertDialogContent>
                      <AlertDialogHeader>
                        <AlertDialogTitle>Delete Hover Report</AlertDialogTitle>
                        <AlertDialogDescription>
                          This will remove the Hover data from all measurement items. Are you sure?
                        </AlertDialogDescription>
                      </AlertDialogHeader>
                      <AlertDialogFooter>
                        <AlertDialogCancel>Cancel</AlertDialogCancel>
                        <AlertDialogAction onClick={handleDeleteHoverReport} className="bg-red-500 hover:bg-red-600">
                          Delete
                        </AlertDialogAction>
                      </AlertDialogFooter>
                    </AlertDialogContent>
                  </AlertDialog>
                </>
              ) : (
                <>
                  <input
                    ref={fileInputRef}
                    type="file"
                    accept=".csv"
                    onChange={handleHoverUpload}
                    className="hidden"
                    disabled={!canEdit || uploading}
                  />
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => fileInputRef.current?.click()}
                    disabled={!canEdit || uploading}
                    className="border-blue-300 text-blue-600 hover:bg-blue-100"
                  >
                    {uploading ? (
                      <>
                        <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                        Uploading...
                      </>
                    ) : (
                      <>
                        <Upload className="h-4 w-4 mr-2" />
                        Upload Hover CSV
                      </>
                    )}
                  </Button>
                </>
              )}
            </div>
          </div>
        </CardHeader>
      </Card>

      {/* Top Summary Strip */}
      <div className="space-y-2">
        <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-7 gap-4">
          <SummaryCard
            title="Hard Measure"
            value={coating_totals?.sqft || 0}
            unit="sqft"
            icon={Paintbrush}
            variant="default"
          />
          <SummaryCard
            title="Hover (Material)"
            value={hover_coating_totals?.sqft || 0}
            unit="sqft"
            icon={FileSpreadsheet}
            variant="coating"
          />
          <SummaryCard
            title="Hard LF"
            value={coating_totals?.lf || 0}
            unit="LF"
            icon={Ruler}
            variant="default"
          />
          <SummaryCard
            title="Hover LF"
            value={hover_coating_totals?.lf || 0}
            unit="LF"
            icon={Ruler}
            variant="coating"
          />
          <SummaryCard
            title="Repair SqFt"
            value={repair_totals?.sqft || 0}
            unit="sqft"
            icon={Wrench}
            variant="repair"
          />
          <SummaryCard
            title="Repair LF"
            value={repair_totals?.lf || 0}
            unit="LF"
            icon={Ruler}
            variant="repair"
          />
          <SummaryCard
            title="Total Items"
            value={data.measurements_output?.items?.length || 0}
            unit="items"
            icon={Hash}
          />
        </div>
        {/* Last Updated - subtle text aligned right */}
        {(updated_at || updated_by) && (
          <p className="text-xs text-slate-400 text-right">
            Last updated {updated_at ? new Date(updated_at).toLocaleDateString() : 'Never'}
            {updated_by && ` by ${updated_by.length > 20 ? updated_by.slice(0, 8) + '...' : updated_by}`}
          </p>
        )}
      </div>

      {/* Section 1: Coating / Paint Measurements */}
      <Card>
        <CardHeader className="pb-4">
          <div className="flex items-center gap-3">
            <div className="p-2 rounded-lg bg-blue-100 dark:bg-blue-900/30">
              <Paintbrush className="h-5 w-5 text-blue-600 dark:text-blue-400" />
            </div>
            <div>
              <CardTitle className="text-lg">Coating / Paint Measurements</CardTitle>
              <CardDescription>
                Measure areas that will receive coating or paint
              </CardDescription>
            </div>
          </div>
        </CardHeader>
        <CardContent className="space-y-3">
          {coating_groups?.map((group) => (
            <MeasurementGroup
              key={group.key}
              group={group}
              category="COATING"
              onAddItem={(groupKey, itemData) => handleAddItem(groupKey, itemData, 'COATING')}
              onUpdateItem={handleUpdateItem}
              onDeleteItem={handleDeleteItem}
              onDuplicateItem={handleDuplicateItem}
              disabled={!canEdit || saving}
            />
          ))}
        </CardContent>
      </Card>

      {/* Section 2: Repair Measurements */}
      <Card>
        <CardHeader className="pb-4">
          <div className="flex items-center gap-3">
            <div className="p-2 rounded-lg bg-amber-100 dark:bg-amber-900/30">
              <Wrench className="h-5 w-5 text-amber-600 dark:text-amber-400" />
            </div>
            <div>
              <CardTitle className="text-lg">Repair Measurements</CardTitle>
              <CardDescription>
                Measure areas requiring repair work (prep, patching, remediation)
              </CardDescription>
            </div>
          </div>
        </CardHeader>
        <CardContent className="space-y-3">
          {repair_groups?.map((group) => (
            <MeasurementGroup
              key={group.key}
              group={group}
              category="REPAIR"
              onAddItem={(groupKey, itemData) => handleAddItem(groupKey, itemData, 'REPAIR')}
              onUpdateItem={handleUpdateItem}
              onDeleteItem={handleDeleteItem}
              onDuplicateItem={handleDuplicateItem}
              disabled={!canEdit || saving}
            />
          ))}
        </CardContent>
      </Card>

      {/* Saving indicator */}
      {saving && (
        <div className="fixed bottom-4 right-4 bg-slate-900 text-white px-4 py-2 rounded-lg flex items-center gap-2 shadow-lg">
          <Loader2 className="h-4 w-4 animate-spin" />
          Saving...
        </div>
      )}
    </div>
  );
};

export default MeasurementsTab;
